import React, { ReactElement } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';

import { ReadOnlyInput, type ReadOnlyInputProps } from '../ReadOnlyInput/ReadOnlyInput';
import { Mode, Option } from '../../../../general-types';

/** Props only for view mode (passed to ReadOnlyInput) */
type ViewOnlyProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> = Pick<ReadOnlyInputProps<TFieldValues, TName>, 'placeholder' | 'renderValue'>;

type ModeAwareInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TOptions extends Option[] | undefined,
> = {
  mode: Mode;
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  options?: TOptions; // optional prop - infers TOptions from what's passed
  readonly?: boolean;
  /** Render callback for edit mode. Receives shared props with inferred options type */
  renderEdit: (props: {
    control: Control<TFieldValues>;
    name: TName;
    label?: string;
    options: TOptions;
  }) => ReactElement;
} & ViewOnlyProps<TFieldValues, TName>;

/**
 * Wrapper component for form inputs that switches between view and edit modes.
 * In VIEW mode renders ReadOnlyInput, in EDIT mode calls renderEdit callback.
 * Options type flows through: if passed, renderEdit gets Option[]; if not, gets undefined.
 */
export const ModeAwareInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
  TOptions extends Option[] | undefined = undefined,
>({
  mode,
  control,
  name,
  label,
  options,
  placeholder = '—',
  readonly,
  renderValue,
  renderEdit,
}: ModeAwareInputProps<TFieldValues, TName, TOptions>) => {
  const sharedProps = {
    control,
    name,
    label,
    options: options as TOptions,
  };

  if (mode === 'VIEW' || readonly) {
    return (
      <ReadOnlyInput
        {...sharedProps}
        placeholder={placeholder}
        renderValue={renderValue}
      />
    );
  }

  return renderEdit(sharedProps);
};
